// Página Colaboradores.js com novo design Tailwind

import React, { useContext, useState } from 'react';
import { TeamContext } from '../store/teamContext';
import TeamForm from '../components/Team/TeamForm';
import TeamTable from '../components/Team/TeamTable';
import PaymentStatus from '../components/Team/PaymentStatus';

// Importe ícones do Lucide
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  Plus, 
  Calendar, 
  FileText, 
  UserPlus,
  Filter,
  ChevronLeft,
  ChevronRight,
  Search
} from 'lucide-react';

const Colaboradores = () => {
  const {
    teamMembers,
    payments,
    totalSalaries,
    addTeamMember,
    removeTeamMember,
    updateTeamMember,
    registerPayment,
    getPaymentStatus
  } = useContext(TeamContext);

  const [editingMember, setEditingMember] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('team');

  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const handleEdit = (member) => {
    setEditingMember(member);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
  };

  const handleSaveEdit = (updatedMember) => {
    updateTeamMember(editingMember.id, updatedMember);
    setEditingMember(null);
  };

  const handleAddMember = (member) => {
    addTeamMember(member);
  };

  const handleRemoveMember = (id) => {
    removeTeamMember(id);
    if (editingMember?.id === id) {
      setEditingMember(null);
    }
  };

  const handlePaymentStatus = (memberId, status) => {
    registerPayment(memberId, currentMonth, currentYear, status);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filtragem de membros por termo de busca
  const filteredMembers = searchTerm
    ? teamMembers.filter(member =>
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.role.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : teamMembers;

  return (
    <div className="page-container">
      <div className="page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="page-title mb-1">Gestão de Colaboradores</h1>
          <p className="text-gray-600">Gerencie sua equipe e controle pagamentos</p>
        </div>
        
        {/* Tabs de Navegação */}
        <div className="flex">
          <nav className="tabs">
            {[
              { id: 'team', label: 'Equipe', icon: <Users className="h-4 w-4 mr-2" /> },
              { id: 'payments', label: 'Pagamentos', icon: <DollarSign className="h-4 w-4 mr-2" /> }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab flex items-center ${activeTab === tab.id ? 'tab-active' : 'tab-inactive'}`}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Cards de métricas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
        <div className="stat-card">
          <div className="flex items-center">
            <div className="stat-icon bg-blue-100">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Total de Colaboradores</p>
              <p className="text-2xl font-bold">{teamMembers.length}</p>
            </div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="flex items-center">
            <div className="stat-icon bg-green-100">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Folha de Pagamento</p>
              <p className="text-2xl font-bold">R$ {totalSalaries.toFixed(2)}</p>
            </div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="flex items-center">
            <div className="stat-icon bg-purple-100">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Média Salarial</p>
              <p className="text-2xl font-bold">
                R$ {teamMembers.length > 0 ? (totalSalaries / teamMembers.length).toFixed(2) : '0.00'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {activeTab === 'team' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="card">
              <div className="card-header">
                <h2 className="card-title m-0 flex items-center">
                  <UserPlus className="h-5 w-5 mr-2 text-blue-600" />
                  {editingMember ? 'Editar Colaborador' : 'Adicionar Novo Colaborador'}
                </h2>
                {editingMember && (
                  <button 
                    onClick={handleCancelEdit}
                    className="btn-outline text-sm"
                  >
                    Cancelar Edição
                  </button>
                )}
              </div>
              <div className="p-6">
                <TeamForm
                  onAddMember={handleAddMember}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={handleCancelEdit}
                  editingMember={editingMember}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h2 className="card-title m-0 flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                  Status de Pagamentos
                </h2>
                <div className="flex items-center space-x-3">
                  <button 
                    className="btn-icon"
                    onClick={() => {
                      let newMonth = currentMonth - 1;
                      let newYear = currentYear;
                      if (newMonth < 0) {
                        newMonth = 11;
                        newYear--;
                      }
                      setCurrentMonth(newMonth);
                      setCurrentYear(newYear);
                    }}
                    aria-label="Mês anterior"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="text-sm font-medium">
                    {months[currentMonth]} {currentYear}
                  </span>
                  <button 
                    className="btn-icon"
                    onClick={() => {
                      let newMonth = currentMonth + 1;
                      let newYear = currentYear;
                      if (newMonth > 11) {
                        newMonth = 0;
                        newYear++;
                      }
                      setCurrentMonth(newMonth);
                      setCurrentYear(newYear);
                    }}
                    aria-label="Próximo mês"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="p-6">
                <PaymentStatus
                  members={teamMembers}
                  month={currentMonth}
                  year={currentYear}
                  onUpdateStatus={handlePaymentStatus}
                  getStatus={getPaymentStatus}
                />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="card-title m-0 flex items-center">
                <Users className="h-5 w-5 mr-2 text-blue-600" />
                Lista de Colaboradores
              </h2>
              <div className="flex space-x-3">
                <div className="relative hidden md:block">
                  <input
                    type="text"
                    placeholder="Buscar colaboradores..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
                <button 
                  className="btn-outline text-sm flex items-center"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filtrar
                </button>
                <button 
                  className="btn-primary text-sm flex items-center"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Novo Colaborador
                </button>
              </div>
            </div>
            
            {/* Buscador mobile */}
            <div className="md:hidden p-4 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar colaboradores..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            <div className="table-container">
              {filteredMembers.length > 0 ? (
                <TeamTable
                  members={filteredMembers}
                  onEdit={handleEdit}
                  onRemove={handleRemoveMember}
                />
              ) : (
                <div className="p-8 text-center">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">
                    {searchTerm
                      ? 'Nenhum colaborador encontrado para sua busca.'
                      : 'Nenhum colaborador cadastrado ainda.'}
                  </p>
                  <button 
                    className="btn-primary flex items-center mx-auto"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Adicionar Colaborador
                  </button>
                </div>
              )}
            </div>
            
            {filteredMembers.length > 0 && (
              <div className="card-footer flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Mostrando {filteredMembers.length} de {teamMembers.length} colaboradores
                </div>
                <div className="flex space-x-2">
                  <button className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                    Anterior
                  </button>
                  <button className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'payments' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title m-0 flex items-center">
              <DollarSign className="h-5 w-5 mr-2 text-blue-600" />
              Controle de Pagamentos
            </h2>
            <div className="flex items-center space-x-3">
              <button 
                className="btn-icon"
                onClick={() => {
                  let newMonth = currentMonth - 1;
                  let newYear = currentYear;
                  if (newMonth < 0) {
                    newMonth = 11;
                    newYear--;
                  }
                  setCurrentMonth(newMonth);
                  setCurrentYear(newYear);
                }}
                aria-label="Mês anterior"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-sm font-medium">
                {months[currentMonth]} {currentYear}
              </span>
              <button 
                className="btn-icon"
                onClick={() => {
                  let newMonth = currentMonth + 1;
                  let newYear = currentYear;
                  if (newMonth > 11) {
                    newMonth = 0;
                    newYear++;
                  }
                  setCurrentMonth(newMonth);
                  setCurrentYear(newYear);
                }}
                aria-label="Próximo mês"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold mb-2">Total a Pagar</h3>
                <p className="text-3xl font-bold text-gray-800">R$ {totalSalaries.toFixed(2)}</p>
                <p className="text-sm text-gray-600 mt-2">Para {teamMembers.length} colaboradores</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold mb-2">Pagamentos Pendentes</h3>
                <p className="text-3xl font-bold text-yellow-600">
                  {teamMembers.filter(member => {
                    const status = getPaymentStatus(member.id, currentMonth, currentYear);
                    return status === 'pending' || !status;
                  }).length}
                </p>
                <p className="text-sm text-gray-600 mt-2">Colaboradores sem pagamento</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold mb-2">Pagamentos Concluídos</h3>
                <p className="text-3xl font-bold text-green-600">
                  {teamMembers.filter(member => 
                    getPaymentStatus(member.id, currentMonth, currentYear) === 'paid'
                  ).length}
                </p>
                <p className="text-sm text-gray-600 mt-2">Colaboradores pagos este mês</p>
              </div>
            </div>
            
            <PaymentStatus
              members={teamMembers}
              month={currentMonth}
              year={currentYear}
              onUpdateStatus={handlePaymentStatus}
              getStatus={getPaymentStatus}
              showDetailed={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Colaboradores;
